/**
 * BankWidget.jsx — Profile-page widget showing the user's coin bank
 * (balance · lifetime earn/spend · last 5 ledger entries). Reads from
 * /api/bank/me. Tapping "View bank" navigates to /bank for the full
 * ledger view.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Coins, ArrowUpRight, ArrowDownRight, Loader2, TrendingUp, TrendingDown,
  Wallet, ChevronRight,
} from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

// Color tokens per direction — keeps the row visually consistent
// across BankWidget + BankPage.
const earnTint = 'text-emerald-300';
const spendTint = 'text-rose-300';

const LedgerRow = ({ entry, dense = false }) => {
  const isEarn = entry.amount >= 0;
  const Icon = isEarn ? ArrowDownRight : ArrowUpRight; // ↘ in / ↗ out
  return (
    <div
      className={`flex items-center gap-3 ${dense ? 'py-1.5' : 'py-2'}`}
      data-testid={`bank-ledger-row-${entry.created_at}`}
    >
      <div className={`w-7 h-7 rounded-lg flex-shrink-0 grid place-items-center border ${
        isEarn ? 'bg-emerald-500/10 border-emerald-400/30' : 'bg-rose-500/10 border-rose-400/30'
      }`}>
        <Icon className={`w-3.5 h-3.5 ${isEarn ? earnTint : spendTint}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-white truncate">
          {entry.label || entry.source}
        </p>
        <p className="text-[10px] text-white/40 uppercase tracking-widest font-bold">
          {formatTime(entry.created_at)}
        </p>
      </div>
      <span className={`tabular-nums text-sm font-extrabold ${isEarn ? earnTint : spendTint}`}>
        {isEarn ? '+' : ''}{entry.amount.toLocaleString()}
      </span>
    </div>
  );
};

const BankWidget = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/bank/me`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (alive) setData(d);
      } catch (e) {
        if (alive) setError(typeof e?.message === 'string' ? e.message : 'Failed to load');
      }
    })();
    return () => { alive = false; };
  }, []);

  if (error) {
    return (
      <div
        className="rounded-2xl border border-rose-400/30 bg-rose-500/5 p-4 text-sm text-rose-200"
        data-testid="bank-widget-error"
      >
        Couldn&apos;t load your bank: {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="rounded-2xl border border-white/10 bg-[#12131A] p-4 flex items-center gap-2 text-sm text-white/60" data-testid="bank-widget-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading bank…
      </div>
    );
  }

  return (
    <section
      className="rounded-2xl border border-amber-400/30 bg-[#12131A] p-5 space-y-4"
      data-testid="bank-widget"
    >
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-400/15 border border-amber-400/30 grid place-items-center">
            <Wallet className="w-4 h-4 text-amber-300" />
          </div>
          <div>
            <p className="eyebrow text-amber-300">Coin bank</p>
            <p className="text-[11px] text-white/50">Every earn · every spend</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/bank')}
          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-bold text-amber-300 hover:text-amber-200"
          data-testid="bank-widget-view-all"
        >
          View bank <ChevronRight className="w-3 h-3" />
        </button>
      </header>

      {/* Balance — the headline number */}
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] font-extrabold text-white/40 mb-1">Balance</p>
          <p className="font-display text-4xl sm:text-5xl font-black text-white leading-none tabular-nums inline-flex items-center gap-2">
            <Coins className="w-7 h-7 text-amber-300" />
            {data.balance.toLocaleString()}
          </p>
        </div>
        <div className="text-right space-y-1">
          <p className={`text-[11px] font-bold inline-flex items-center gap-1 ${earnTint}`}>
            <TrendingUp className="w-3 h-3" />
            +{data.lifetime_earned.toLocaleString()} earned
          </p>
          <p className={`text-[11px] font-bold inline-flex items-center gap-1 ${spendTint}`}>
            <TrendingDown className="w-3 h-3" />
            -{data.lifetime_spent.toLocaleString()} spent
          </p>
        </div>
      </div>

      {/* Recent ledger */}
      {data.recent_entries && data.recent_entries.length > 0 && (
        <div className="border-t border-white/5 pt-3">
          <p className="eyebrow text-white/45 mb-1">Latest activity</p>
          <div className="divide-y divide-white/5">
            {data.recent_entries.map((e, i) => (
              <LedgerRow key={`${e.created_at}-${i}`} entry={e} dense />
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default BankWidget;
